import {StateController} from "@lit-app/state/src/state-controller";
import {virtualize} from "@lit-labs/virtualizer/virtualize.js";
import {html, HTMLTemplateResult, TemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {translate} from "lit-translate";
import {takeUntil} from "rxjs/operators";
import {container} from "tsyringe";

import {AbstractO2dElement} from '@o2d/shared';
import {appState} from "../../../app.state";
import "../knowledge-object-item/knowledge-object-item.component";
import {EntityLiveRefreshController} from "../../_reactive-controllers/entity-live-refresh.controller";
import {RxjsLifecycleController} from "../../_reactive-controllers/rxjs-lifecycle.controller";
import type {KnowledgeObjectReference} from "../knowledge-object-item/knowledge-object-item.component";
import {BackendClient} from "../../../connectivity/backend-client";
import {DialogHelper} from "../../../helpers/dialog.helper";
// NOT `import type`: used as a VALUE below (`getEntityClass: () => KnowledgeObject`),
// not just as a type annotation — a type-only import here gets erased by the
// bundler and `KnowledgeObject` becomes a dangling reference at runtime,
// throwing inside EntityLiveRefreshController.hostConnected() (called
// synchronously from `super.connectedCallback()`) and silently aborting the
// rest of `connectedCallback()` — including the `reload()` call the list
// depends on. (Regression found 2026-08-17: this is exactly what made the
// list render empty after the move out of knowledge-overview-view.)
import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {hasKnowledgeDraft, KnowledgeDraftChangeNotifier} from "../../../domain/helpers/knowledge-draft-registry.helper";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {NavigationHelper} from "../../../helpers/navigation.helper";
import {SlugHelper} from "../../../helpers/slug.helper";
import {KeyboardActivationHelper} from "../../../helpers/keyboard-activation.helper";

/** KnowledgeObject.type of an image asset (created when an image is pasted into
 *  a whiteboard or document). Grouped under the collapsible "Assets" folder. */
const IMAGE_TYPE = 'image';

/** The link type the content save maintains for embedded objects. */
const EMBEDS_LINK_TYPE = 'embeds';

/** The shape `/api/v1/query/knowledge-link` answers with; uids are short ids. */
interface KnowledgeLinkDTO {
    readonly sourceUid: string;
    readonly targetUid: string;
    readonly linkType: string;
}

/**
 * One row in the overview list. The list has no real (backend) folders, so the
 * "Assets" folder is a pure view-model construct: a synthetic header row that
 * groups all image objects, plus the object rows themselves.
 */
type DisplayRow =
    | {readonly kind: 'object'; readonly item: KnowledgeObject}
    | {readonly kind: 'assets-folder'; readonly count: number};

/**
 * Persistent Wissen-mode sidebar content: create button and the virtualized
 * KnowledgeObject list — living in `ha-app`'s `<sidebar>` (right under
 * `<nav role="navigation">`) instead of inside the routed
 * `knowledge-overview-view`, so it survives navigation between `/knowledge`
 * and `/knowledge/:sid/:slug` (operator request 2026-08-17: previously this
 * lived in a collapsible "Filterleiste" drawer inside the view itself).
 *
 * The filter FORM (`<ha-form-knowledge-filters>`, type combobox + title
 * search) does NOT render here anymore (Stefan, 2026-08-30) — it moved into
 * a `.sidebar__filter` drawer in `knowledge-overview-view`, mirroring
 * `ha-tasklist.view.ts`'s `ha-form-issue-filters` drawer. This component
 * still LISTENS to `appState.knowledgeFilters` (see `connectedCallback`
 * below) and still calls `_repo.reload(filters)` on every change — the state
 * lives in `appState`, not in the DOM position of the form, so moving the
 * form elsewhere required no change to that wiring.
 *
 * Owns the list/selection concerns that `knowledge-overview-view` used to
 * own; that view now only resolves the ROUTE's `sid` against
 * `KnowledgeObjectRepository.items$` (a singleton `BehaviorSubject`, so both
 * components observe the same data without a duplicate fetch — this
 * component is the only one calling `reload()`) to render the right-hand
 * `knowledge-view-pane`.
 */
@customElement('knowledge-sidebar-list')
export class KnowledgeSidebarList extends AbstractO2dElement {

    /**
     * The currently ROUTED object's uid (from `ha-app`'s parsed URL), or
     * `null` on the bare `/knowledge` list. Drives which row renders
     * `.selected` — this component never tracks selection itself, the URL
     * is the single source of truth (so a deep link, browser back/forward,
     * or the auto-restore below all just work through the same property).
     */
    @property({type: String, attribute: 'selected-sid'})
    public selectedSid: string | null = null;

    @state()
    private _items: KnowledgeObject[] = [];

    /**
     * Whether the collapsible "Assets" folder (image objects) is expanded.
     * Default collapsed so the images pasted into whiteboards/documents stay out
     * of the way and the top level shows only whiteboards + documents (Anke,
     * 2026-07-28).
     */
    @state()
    private _assetsExpanded = false;

    /**
     * Which objects embed each image, keyed by the image's uid. Empty until the
     * assets folder is opened for the first time; a missing entry renders no cell
     * rather than a wrong zero.
     */
    @state()
    private _referencesByImage: ReadonlyMap<string, KnowledgeObjectReference[]> = new Map();

    /** Re-entrancy guard for the reference load — see `_loadReferences`. */
    private _referencesLoading = false;

    /** Re-entrancy guard for the "+ Neu" button — a click while the create
     *  dialog is already opening must not queue a second one. */
    private _creating = false;

    /** Required by EntityLiveRefreshController for reconnect-safe auto-cleanup. */
    public readonly rxjs = new RxjsLifecycleController(this);

    // Re-render on appState.knowledgeFilters changes (written by
    // ha-form-knowledge-filters) so a new filter reloads the list.
    private readonly _stateController = new StateController(this, appState);

    private readonly _repo: KnowledgeObjectRepository = container.resolve(KnowledgeObjectRepository);

    private readonly _liveRefreshController = new EntityLiveRefreshController(this, {
        getEntityClass: () => KnowledgeObject,
        onReload: () => this._repo.reload(appState.knowledgeFilters),
    });

    /**
     * Subscribes to the repository's live `items$` stream and triggers the
     * initial list load. Mirrors the mount lifecycle the list used to have as
     * part of `knowledge-overview-view`, just moved here — this component now
     * mounts/unmounts with Wissen mode itself (`ha-app`), not with the
     * `/knowledge*` route, so it stays alive across `/knowledge` <->
     * `/knowledge/:sid/:slug` navigation.
     */
    public connectedCallback(): void {
        super.connectedCallback();

        // A deep link (bookmark, shared /knowledge/:sid/:slug URL, or a
        // reload while routed there) mounts this component with
        // `selectedSid` already set — the row highlights correctly, but
        // without this mirror `lastKnowledgeObjectSid` stays on its OLD
        // value, so leaving Wissen and returning silently restores the wrong
        // object. Mirrors exactly what `_onSelect` does for a click, for the
        // mount case (lost when this logic moved out of the old
        // knowledge-overview-view.connectedCallback(), stefan-todos finding
        // 0001-sidebar-restructure-02, MAJOR).
        if (this.selectedSid) {
            appState.lastKnowledgeObjectSid = this.selectedSid;
        }

        this._repo.items$
            .pipe(takeUntil(this.rxjs.disconnected$))
            .subscribe(items => {
                // Spread to a new array reference so the virtualizer detects
                // changes via identity comparison (see PROGRESS Risk note).
                this._items = [...items];
                this._maybeRestoreLastSelection();
            });

        // Reload the list whenever the persisted filters change — covers the
        // form-driven path (ha-form-knowledge-filters writes appState.knowledgeFilters;
        // its two fields each carry their own with-clear-button). The form
        // itself lives in a `.sidebar__filter` drawer in
        // `knowledge-overview-view` (Stefan, 2026-08-30), not in this
        // component anymore — this listener still fires regardless, since
        // it's driven by appState, not by the form's DOM position.
        appState.listen$('knowledgeFilters')
            .pipe(takeUntil(this.rxjs.disconnected$))
            .subscribe(filters => void this._repo.reload(filters));

        // Anke, 2026-09-16: unsaved-changes indicator (italic + superscript
        // asterisk, same treatment as the open document's own toolbar
        // filename). `hasKnowledgeDraft()` is a read-only localStorage
        // check (never writes or removes) recomputed on every render — this subscription is only the LIVE
        // update signal for "a draft was written/cleared just now" (a plain
        // `requestUpdate()`, not a data fetch); see that notifier's own doc
        // comment for why a native `storage` event cannot substitute
        // (same-tab writes never fire it).
        container.resolve(KnowledgeDraftChangeNotifier).changed$
            .pipe(takeUntil(this.rxjs.disconnected$))
            .subscribe(() => this.requestUpdate());

        void this._repo.reload(appState.knowledgeFilters);
    }

    /**
     * On a no-selection mount (bare `/knowledge`), redirects to the last opened
     * object once the items have loaded — only when that object still exists in
     * the current (filtered) list. Idempotent: only fires the first time
     * `_items` arrives non-empty; an already-routed `selectedSid` short-circuits.
     */
    private _restoreFired = false;

    private _maybeRestoreLastSelection(): void {
        if (this._restoreFired || this.selectedSid || this._items.length === 0) {
            return;
        }
        const lastSid = appState.lastKnowledgeObjectSid;
        if (!lastSid) {
            return;
        }
        const target = this._items.find(ko => ko.uid === lastSid);
        if (!target) {
            return; // Stale UID (deleted, filtered out) — leave the user on /knowledge.
        }
        this._restoreFired = true;
        const slug = SlugHelper.toKebabCase(target.title);
        NavigationHelper.goto(slug ? `/knowledge/${lastSid}/${slug}` : `/knowledge/${lastSid}`);
    }

    protected render(): HTMLTemplateResult {
        return html`
            <button type="button"
                    title=${translate('wissen.overview.toolbar.new')}
                    class="knowledge-add add-entity buttonform-line"
                    data-label=${translate('wissen.overview.toolbar.new')}
                    @click=${this._onCreateKnowledgeObject}>
                <div class="hover-effect"></div>
            </button>
            <div class="knowledge-overview-list"
                 @knowledge-object-selected=${this._onSelect}>
                ${virtualize({
                    items: this._buildDisplayRows(),
                    scroller: true,
                    renderItem: (row) => this._renderRow(row as DisplayRow),
                })}
            </div>
        `;
    }

    /**
     * Opens the knowledge-object edit dialog in create-mode (null payload). The
     * dialog offers a type choice (Whiteboard | Textdokument), so this single
     * button creates either type. Re-entry guard prevents two rapid clicks from
     * opening the dialog twice; the dialog itself owns post-create navigation
     * and entity publishing.
     */
    private _onCreateKnowledgeObject = async (): Promise<void> => {
        if (this._creating) {
            return;
        }
        this._creating = true;
        try {
            await DialogHelper.showDialog('ha-dialog-knowledge-object-edit', null);
        } catch (error) {
            console.error(`Failed to open knowledge-object create dialog.`, error);
        } finally {
            this._creating = false;
        }
    };

    /**
     * Derives the visible rows from the flat item list: whiteboards and
     * documents stay at the top level; all image objects are grouped under a
     * single collapsible "Assets" folder row and only rendered when expanded.
     * When there are no images the folder row is omitted entirely. Pure
     * view-model — the backend has no folder concept.
     */
    private _buildDisplayRows(): DisplayRow[] {
        const objects: DisplayRow[] = [];
        const images: DisplayRow[] = [];
        for (const item of this._items) {
            if (item.type === IMAGE_TYPE) {
                images.push({kind: 'object', item});
            } else {
                objects.push({kind: 'object', item});
            }
        }
        if (images.length === 0) {
            return objects;
        }
        const rows: DisplayRow[] = [...objects, {kind: 'assets-folder', count: images.length}];
        if (this._assetsExpanded) {
            rows.push(...images);
        }
        return rows;
    }

    /** Renders one display row — either the Assets folder header or an object. */
    private _renderRow(row: DisplayRow): TemplateResult {
        if (row.kind === 'assets-folder') {
            return this._renderAssetsFolder(row.count);
        }
        // Image objects live under the folder -> render them indented when shown.
        return this._renderItem(row.item, row.item.type === IMAGE_TYPE);
    }

    /**
     * The collapsible "Assets" folder header. Clicking toggles
     * `_assetsExpanded`, which changes the rows `_buildDisplayRows` emits; the
     * chevron reflects the open/closed state. Reuses the object-item layout
     * cells so it lines up with the rows above it.
     */
    private _renderAssetsFolder(count: number): TemplateResult {
        const chevron = this._assetsExpanded ? 'icon-chevron-down' : 'icon-chevron-right';
        return html`
            <div class="knowledge-assets-folder ${this._assetsExpanded ? 'expanded' : ''}"
                 role="button"
                 tabindex="0"
                 aria-expanded=${this._assetsExpanded ? 'true' : 'false'}
                 @click=${this._onToggleAssets}
                 @keydown=${this._onAssetsKeydown}>
                <div class="knowledge-object-item__icon-cell">
                    <i class="icon ${chevron} knowledge-assets-folder__chevron" aria-hidden="true"></i>
                </div>
                <div class="knowledge-object-item__main-cell">
                    <div class="knowledge-object-item__title">Assets</div>
                </div>
                <div class="knowledge-assets-folder__count">${count}</div>
            </div>
        `;
    }

    /** Toggles the Assets folder open/closed, loading the reference counts on open. */
    private _onToggleAssets = (): void => {
        this._assetsExpanded = !this._assetsExpanded;
        if (this._assetsExpanded) {
            void this._loadReferences();
        }
    };

    /**
     * Loads, once per opening, which objects embed each image.
     *
     * Done here rather than while rendering: the list is virtualized and its row
     * renderer runs on every scroll, so a request in there would fire without end.
     *
     * The map is REPLACED, never mutated — Lit compares by identity, and a
     * `map.set()` would arrive without triggering a re-render, so the numbers
     * would sit there invisible.
     *
     * Titles and types come from `_items`, which the view has already loaded. That
     * costs no extra request and filters soft-deleted hosts for free: a deleted
     * whiteboard is not in the list, and it no longer uses the picture — counting
     * it would answer a question nobody asked.
     */
    private async _loadReferences(): Promise<void> {
        // A second opening while the first is still loading would race two answers
        // into the same map; the later one wins for no reason.
        if (this._referencesLoading) {
            return;
        }
        this._referencesLoading = true;

        try {
            const images = this._items.filter(item => item.type === IMAGE_TYPE);
            const byUid = new Map(this._items.map(item => [item.uid, item]));
            const client = container.resolve(BackendClient);

            const entries = await Promise.all(images.map(async image => {
                const response = await client.post<KnowledgeLinkDTO[]>(
                    `/api/v1/query/knowledge-link`, false, {hostSid: image.uid},
                );
                const hosts = (response ?? [])
                    .filter(link => link.linkType === EMBEDS_LINK_TYPE && link.targetUid === image.uid)
                    .map(link => byUid.get(link.sourceUid))
                    .filter((host): host is KnowledgeObject => host !== undefined)
                    .map(host => ({title: host.title, type: host.type}));

                return [image.uid, hosts] as const;
            }));

            this._referencesByImage = new Map(entries);
        } catch (error) {
            // The folder stays without numbers rather than without contents. The
            // counts are an addition to the list, and losing them must not cost
            // the user the list itself.
            console.error(`Failed to load the reference counts of the assets folder.`, error);
        } finally {
            this._referencesLoading = false;
        }
    }

    /** Keyboard access for the folder row (Enter / Space toggle). */
    private readonly _onAssetsKeydown = KeyboardActivationHelper.onEnterOrSpace(() => this._onToggleAssets());

    private _renderItem(item: KnowledgeObject, nested = false): TemplateResult {
        // Only images carry the reference cell, and only once their counts have
        // been loaded — `null` is "not asked", which the row renders as no cell.
        const references = item.type === IMAGE_TYPE
            ? this._referencesByImage.get(item.uid) ?? null
            : null;

        return html`
            <knowledge-object-item
                class=${nested ? 'knowledge-assets-child' : ''}
                .item=${item}
                .references=${references}
                .selected=${item.uid === this.selectedSid}
                .hasDraft=${hasKnowledgeDraft(item.uid, appState.user?.uid ?? '')}>
            </knowledge-object-item>
        `;
    }

    /**
     * Handles a `knowledge-object-selected` event bubbling up from a
     * `knowledge-object-item`: deep-links to the clicked object's
     * `/knowledge/:sid/:slug` route. Selection itself is NOT tracked here —
     * `selectedSid` (from the URL, via `ha-app`) drives it, so the router
     * navigation below is what actually changes the highlighted row.
     */
    private _onSelect(event: Event): void {
        const detail = (event as CustomEvent<{sid: string}>).detail;
        if (!detail?.sid) {
            return;
        }
        // Persist the selection so re-entering /knowledge restores it.
        appState.lastKnowledgeObjectSid = detail.sid;
        const selected = this._items.find(ko => ko.uid === detail.sid);
        const slug = selected ? SlugHelper.toKebabCase(selected.title) : '';
        // R1.3: avoid trailing slash when the title strips to an empty slug.
        NavigationHelper.goto(slug ? `/knowledge/${detail.sid}/${slug}` : `/knowledge/${detail.sid}`);
    }
}
